import {
  SplitSupplyDayType,
  SplitSupplyForm,
  SplitSupplySelfRole,
} from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import type { TFunction } from 'i18next'

import {
  type BasePatternValue,
  DAY_TYPES,
  isUniformValues,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import { slotRangeLabel } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/slotTime'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

const NS = 'page:adminOcctoMaster.splitSupplyContract' as const

/** 供給形態の表示名。 */
export function supplyFormLabel(t: TFunction, form: SplitSupplyForm): string {
  switch (form) {
    case SplitSupplyForm.FIXED:
      return t(`${NS}.supplyForm.fixed`)
    case SplitSupplyForm.TIME_SPLIT:
      return t(`${NS}.supplyForm.timeSplit`)
    case SplitSupplyForm.VARIABLE:
      return t(`${NS}.supplyForm.variable`)
    case SplitSupplyForm.UNSPECIFIED:
    default:
      return '—'
  }
}

/**
 * 供給形態の説明 (一覧のバッジの hover)。 時間分割型は自社の担当を時刻で添える。
 */
export function supplyFormHint(
  t: TFunction,
  form: SplitSupplyForm,
  ownSlotFrom: number | undefined,
  ownSlotTo: number | undefined,
): string {
  switch (form) {
    case SplitSupplyForm.FIXED:
      return t(`${NS}.supplyFormHint.fixed`)
    case SplitSupplyForm.TIME_SPLIT:
      return t(`${NS}.supplyFormHint.timeSplit`, {
        range: slotRangeLabel(ownSlotFrom ?? 0, ownSlotTo ?? 47),
      })
    case SplitSupplyForm.VARIABLE:
      return t(`${NS}.supplyFormHint.variable`)
    case SplitSupplyForm.UNSPECIFIED:
    default:
      return ''
  }
}

/** 自社の立場の表示名。 */
export function selfRoleLabel(t: TFunction, role: SplitSupplySelfRole): string {
  switch (role) {
    case SplitSupplySelfRole.LOAD_FOLLOWING:
      return t(`${NS}.selfRole.loadFollowing`)
    case SplitSupplySelfRole.BASE:
      return t(`${NS}.selfRole.base`)
    case SplitSupplySelfRole.UNSPECIFIED:
    default:
      return '—'
  }
}

/** 自社の立場の説明 (一覧のバッジの hover)。 */
export function selfRoleHint(t: TFunction, role: SplitSupplySelfRole): string {
  switch (role) {
    case SplitSupplySelfRole.LOAD_FOLLOWING:
      return t(`${NS}.selfRoleHint.loadFollowing`)
    case SplitSupplySelfRole.BASE:
      return t(`${NS}.selfRoleHint.base`)
    case SplitSupplySelfRole.UNSPECIFIED:
    default:
      return ''
  }
}

/** 日種の表示名 (平日 / 土 / 日 / 祝)。 */
export function dayTypeLabel(t: TFunction, day: SplitSupplyDayType): string {
  switch (day) {
    case SplitSupplyDayType.WEEKDAY:
      return t(`${NS}.dayType.weekday`)
    case SplitSupplyDayType.SATURDAY:
      return t(`${NS}.dayType.saturday`)
    case SplitSupplyDayType.SUNDAY:
      return t(`${NS}.dayType.sunday`)
    case SplitSupplyDayType.HOLIDAY:
      return t(`${NS}.dayType.holiday`)
    case SplitSupplyDayType.UNSPECIFIED:
    default:
      return ''
  }
}

/** 適用期間 ("2026/10/01〜2027/03/31")。 終了日が無い時は開始日から無期限 ("2026/10/01〜")。 */
export function patternPeriodLabel(p: BasePatternValue): string {
  const from = formatCivilDate(p.validFrom)
  return p.validTo === undefined
    ? `${from}〜`
    : `${from}〜${formatCivilDate(p.validTo)}`
}

/** 日種 ("平日・祝")。 */
export function patternDayTypesLabel(
  t: TFunction,
  dayTypes: readonly SplitSupplyDayType[],
): string {
  return DAY_TYPES.filter((d) => dayTypes.includes(d))
    .map((d) => dayTypeLabel(t, d))
    .join('・')
}

/** 月 ("6・7・8・9 月")。 空は全月。 */
export function patternMonthsLabel(
  t: TFunction,
  months: readonly number[],
): string {
  if (months.length === 0) return t(`${NS}.basePattern.allMonths`)
  return t(`${NS}.basePattern.monthsList`, { months: months.join('・') })
}

/** 時間帯 ("08:00〜22:00")。 両方無ければ終日。 */
export function patternSlotRangeLabel(
  t: TFunction,
  slotFrom: number | undefined,
  slotTo: number | undefined,
): string {
  if (slotFrom === undefined && slotTo === undefined) {
    return t(`${NS}.basePattern.allDay`)
  }
  return slotRangeLabel(slotFrom ?? 0, slotTo ?? 47)
}

/** 値 (全コマ同値 / 48 点)。 */
export function patternValuesLabel(
  t: TFunction,
  values: readonly number[],
): string {
  return isUniformValues(values)
    ? t(`${NS}.basePattern.uniformValue`, {
        value: (values[0] ?? 0).toLocaleString(),
      })
    : t(`${NS}.basePattern.perSlotValue`)
}
